// Tamil-Katral-Maiyam\src\pages\admin\StudentsList.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';
import { Select } from '../../components/ui/Select';
import { Input } from '../../components/ui/Input';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { formatDate } from '../../utils/format';
import {
  validateEmail,
  validatePersonName,
  stripDigitsFromName,
  sanitizeAdmissionNumber,
} from '../../utils/validation';
import { useAuth } from '../../contexts/AuthContext';
import { getSchoolProfile, inviteStudent, listCreatedInvites, CreatedInvite } from '../../modules/admin/api/school.client';
import {
  listStudents,
  createStudent,
  CreateStudentPayload,
  RosterStudent,
  createEnrollment,
  CreateEnrollmentPayload,
  listClasses,
  RosterClass,
  listSections,
  RosterSection,
} from '../../modules/roster/api/roster.client';
import { ROUTES } from '../../utils/constants';
import { StudentBulkImportModal } from '../../components/admin/StudentBulkImportModal';
import { ScrollableSelect } from '../../components/ui/ScrollableSelect';
import { Eye, Mail, UserPlus } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const EMAIL_MAX_LENGTH = 100;

export const StudentsList: React.FC = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState<RosterStudent[]>([]);
  const [isImportModalOpen, setImportModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [addStudentErrors, setAddStudentErrors] = useState<Record<string, string>>({});
  const [studentForm, setStudentForm] = useState<CreateStudentPayload>({
    first_name: '',
    last_name: '',
    display_name: '',
    email: '',
    phone: '',
    admission_no: '',
    gender: '',
    dob: '',
  });
  const { user } = useAuth();
  const schoolId = user?.memberships?.[0]?.school_id;
  const [schoolName, setSchoolName] = useState('');
  const [sendInviteNow, setSendInviteNow] = useState(true);
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteTarget, setInviteTarget] = useState<RosterStudent | null>(null);
  const [inviteForm, setInviteForm] = useState({ email: '', invitee_name: '', notes: '' });
  const [isInviting, setIsInviting] = useState(false);
  const [studentInvites, setStudentInvites] = useState<CreatedInvite[]>([]);
  const inviteByEmail = useMemo(() => {
    const map: Record<string, CreatedInvite> = {};
    studentInvites.forEach((invite) => {
      const emailKey = invite.email?.trim().toLowerCase();
      if (emailKey) {
        map[emailKey] = invite;
      }
    });
    return map;
  }, [studentInvites]);
  const inviteCount = studentInvites.length;
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [selectedStudentForEnroll, setSelectedStudentForEnroll] = useState<RosterStudent | null>(null);
  const [classes, setClasses] = useState<RosterClass[]>([]);
  const [sections, setSections] = useState<RosterSection[]>([]);
  const [enrollForm, setEnrollForm] = useState<CreateEnrollmentPayload>({
    student_id: '',
    class_id: '',
    section_id: '',
    academic_year_key: '',
  });
  const [isEnrolling, setIsEnrolling] = useState(false);

  // Filters (client-side)
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createdFromDate, setCreatedFromDate] = useState('');

  // ✅ Placeholders (ALL form fields)
  const placeholders = useMemo(
    () => ({
      first_name: 'Enter your first name',
      last_name: 'Enter your last name',
      display_name: 'Enter your display name',
      admission_no: 'Enter admission number',
      email: 'name@gmail.com',
      phone: 'Enter phone number',
      gender: 'Select gender',
      dob: 'Select date of birth',
      invite_email: 'name@gmail.com',
      invitee_name: 'Enter invitee name',
      invite_notes: 'Enter notes (optional)',
      enroll_academic_year: 'e.g., 2025-2026',
      enroll_class: 'Select class',
      enroll_section: 'Select section',
    }),
    []
  );

  const loadStudents = async () => {
    setLoading(true);
    try {
      const items = await listStudents({ limit: 50, skip: 0 });
      setStudents(items);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudents();
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    const fetchSchool = async () => {
      try {
        const profile = await getSchoolProfile(schoolId);
        setSchoolName(profile.name || '');
      } catch {
        // ignore
      }
    };
    fetchSchool();
  }, [schoolId]);

  const loadStudentInvites = async () => {
    if (!schoolId) return;
    try {
      const invites = await listCreatedInvites(schoolId, 'student');
      setStudentInvites(invites);
    } catch (error) {
      console.error('Failed to load student invites', error);
    }
  };

  useEffect(() => {
    loadStudentInvites();
  }, [schoolId]);

  const loadClasses = async () => {
    try {
      const items = await listClasses({ limit: 100, skip: 0 });
      setClasses(items);
    } catch (error) {
      showErrorToast(error);
    }
  };

  const loadSections = async () => {
    try {
      const items = await listSections({ limit: 100, skip: 0 });
      setSections(items);
    } catch (error) {
      showErrorToast(error);
    }
  };

  useEffect(() => {
    if (isEnrollModalOpen) {
      loadClasses();
      loadSections();
    }
  }, [isEnrollModalOpen]);

  const getFullName = (s: RosterStudent) =>
    `${s.first_name || ''} ${s.last_name || ''}`.trim() || '-';

  const handleCreateSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAddStudentErrors({});
    const errors: Record<string, string> = {};
    const firstName = (studentForm.first_name ?? '').trim();
    const lastName = (studentForm.last_name ?? '').trim();

    if (!firstName) {
      errors.first_name = 'First name is required';
    } else {
      const r = validatePersonName(studentForm.first_name ?? '');
      if (!r.valid) errors.first_name = 'First name should not contain numbers';
    }
    if (lastName) {
      const r = validatePersonName(studentForm.last_name ?? '');
      if (!r.valid) errors.last_name = 'Last name should not contain numbers';
    }

    const admissionNo = (studentForm.admission_no ?? '').trim();
    if (!admissionNo) {
      errors.admission_no = 'Admission number is required';
    } else if (!/^\d+$/.test(admissionNo)) {
      errors.admission_no = 'Admission number should contain only numbers';
    }

    const emailTrim = (studentForm.email ?? '').trim();
    if (emailTrim) {
      if (emailTrim.length > EMAIL_MAX_LENGTH) {
        errors.email = `Email must be at most ${EMAIL_MAX_LENGTH} characters`;
      } else if (!validateEmail(emailTrim)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    if (studentForm.phone && /[a-zA-Z]/.test(studentForm.phone)) {
      errors.phone = 'Phone should contain only numbers and country code';
    }

    if (Object.keys(errors).length > 0) {
      setAddStudentErrors(errors);
      const firstMessage = Object.values(errors)[0];
      if (firstMessage) showErrorToast(firstMessage);
      return;
    }

    const display_name = [studentForm.first_name, studentForm.last_name]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    setIsCreating(true);
    try {
      const payload: CreateStudentPayload = { ...studentForm, display_name };
      const createdStudent = await createStudent(payload);
      let inviteError = false;
      if (sendInviteNow && schoolId) {
        try {
          const newStudentId: string = resolveStudentId(createdStudent);
          if (!newStudentId) {
            throw new Error('Unable to resolve student id for invite');
          }
          const inviteEmail = studentForm.email?.trim();
          if (!inviteEmail) {
            throw new Error('Email is required to send an invite');
          }
          await inviteStudent(schoolId, {
            email: inviteEmail,
            invitee_name: display_name || `${studentForm.first_name} ${studentForm.last_name}`.trim(),
            notes: `Invite student to ${schoolName || 'your school'}`,
            metadata: { student_id: newStudentId },
          });
        } catch (error) {
          inviteError = true;
          showErrorToast('Student created but invite failed');
        }
      }
      if (!inviteError) {
        showSuccessToast(sendInviteNow ? 'Student created and invite sent' : 'Student created');
      }
      setAddModalOpen(false);
      setAddStudentErrors({});
      setStudentForm({
        first_name: '',
        last_name: '',
        display_name: '',
        email: '',
        phone: '',
        admission_no: '',
        gender: 'male',
        dob: '',
      });
      await loadStudents();
      await loadStudentInvites();
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsCreating(false);
    }
  };

  const getInviteDetails = (student: RosterStudent) => {
    if (student.platform_user_id) return { label: 'Joined', joined: true, invite: null };
    const emailKey = (student.email || '').trim().toLowerCase();
    const invite = inviteByEmail[emailKey];
    if (invite?.status === 'accepted') return { label: 'Joined', joined: true, invite };
    if (invite) return { label: `Invited (${invite.status})`, joined: false, invite };
    return { label: 'Not invited', joined: false, invite: null };
  };

  const resolveStudentId = (student?: RosterStudent): string => student?._id ?? student?.id ?? '';

  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      const fullName = getFullName(s).toLowerCase();
      const email = (s.email || '').toLowerCase();
      const phone = (s.phone || '').replace(/\s/g, '');
      const search = searchText.trim().toLowerCase().replace(/\s/g, '');
      if (search) {
        const matchSearch =
          fullName.includes(search) ||
          email.includes(search) ||
          phone.includes(search);
        if (!matchSearch) return false;
      }
      if (statusFilter && (s.status || '') !== statusFilter) return false;
      if (createdFromDate && s.created_at) {
        const created = s.created_at.slice(0, 10);
        if (created < createdFromDate) return false;
      }
      return true;
    });
  }, [students, searchText, statusFilter, createdFromDate]);

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    students.forEach((s) => {
      if (s.status) set.add(s.status);
    });
    return [{ value: '', label: 'All statuses' }, ...Array.from(set).map((v) => ({ value: v, label: v }))];
  }, [students]);

  const hasActiveFilters = Boolean(searchText.trim() || statusFilter || createdFromDate);
  const clearFilters = () => {
    setSearchText('');
    setStatusFilter('');
    setCreatedFromDate('');
  };

  const openInviteModal = (student: RosterStudent) => {
    setInviteTarget(student);
    setInviteForm({
      email: student.email || '',
      invitee_name:
        student.display_name || `${student.first_name || ''} ${student.last_name || ''}`.trim(),
      notes: '',
    });
    setInviteModalOpen(true);
  };

  const handleInviteSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!schoolId) {
      showErrorToast('School context missing');
      return;
    }
    setIsInviting(true);
    try {
      const targetId: string = resolveStudentId(inviteTarget ?? undefined);
      if (!targetId) {
        showErrorToast('Student identifier missing for invite');
        return;
      }
      await inviteStudent(schoolId, {
        email: inviteForm.email,
        invitee_name: inviteForm.invitee_name,
        notes: inviteForm.notes || `Invite student to ${schoolName || 'your school'}`,
        metadata: { student_id: targetId },
      });
      showSuccessToast('Invite sent');
      setInviteModalOpen(false);
      setInviteTarget(null);
      setInviteForm({ email: '', invitee_name: '', notes: '' });
      await loadStudents();
      await loadStudentInvites();
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsInviting(false);
    }
  };

  const openEnrollModal = (student: RosterStudent) => {
    const studentId = resolveStudentId(student);
    setSelectedStudentForEnroll(student);
    setEnrollForm({
      student_id: studentId,
      class_id: '',
      section_id: '',
      academic_year_key: '',
    });
    setIsEnrollModalOpen(true);
  };

  const handleClassChangeForEnroll = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const classId = event.target.value;
    const selectedClass = classes.find((cls) => cls.id === classId);
    setEnrollForm((prev) => ({
      ...prev,
      class_id: classId,
      section_id: '',
      academic_year_key: selectedClass?.academic_year_key ?? prev.academic_year_key,
    }));
  };

  const handleSectionChangeForEnroll = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const sectionId = event.target.value;
    const selectedSection = sections.find((sec) => sec.id === sectionId);
    setEnrollForm((prev) => ({
      ...prev,
      section_id: sectionId,
      academic_year_key: selectedSection?.academic_year_key ?? prev.academic_year_key,
    }));
  };

  const handleEnrollSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!enrollForm.student_id || !enrollForm.class_id || !enrollForm.academic_year_key) {
      showErrorToast('Student, class, and academic year are required');
      return;
    }
    setIsEnrolling(true);
    try {
      await createEnrollment(enrollForm);
      showSuccessToast('Student enrolled');
      setIsEnrollModalOpen(false);
      setSelectedStudentForEnroll(null);
      setEnrollForm({
        student_id: '',
        class_id: '',
        section_id: '',
        academic_year_key: '',
      });
      await loadStudents();
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsEnrolling(false);
    }
  };

  const filteredSections = useMemo(() => {
    if (!enrollForm.class_id) return [];
    return sections.filter((sec) => sec.class_id === enrollForm.class_id);
  }, [sections, enrollForm.class_id]);

  return (
    <Layout>
      <PageHeader
        title="Students"
        subtitle="View enrolled students"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setImportModalOpen(true)}>
              Import
            </Button>
            <Button onClick={() => setAddModalOpen(true)}>Add Student</Button>
          </div>
        }
      />

      <Card>
        <CardContent padding="md">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center mb-4">
            <div className="md:col-span-6">
              <Input
                placeholder="Search by name, email, phone..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="h-11 w-full"
              />
            </div>
            <div className="md:col-span-3 w-full [&_button]:h-11">
              <ScrollableSelect
                options={statusOptions}
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="Status"
                maxListHeight="10rem"
                className="w-full"
              />
            </div>
            <div className="md:col-span-3">
              <Input
                type="date"
                value={createdFromDate}
                onChange={(e) => setCreatedFromDate(e.target.value)}
                className="h-11 w-full"
              />
            </div>
            {hasActiveFilters && (
              <div className="col-span-1 md:col-span-12 flex md:justify-end">
                <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                  Clear
                </Button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-10">
              <Spinner size="lg" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Full Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStudents.map((student) => {
                  const { joined, invite } = getInviteDetails(student);
                  const hasEmail = Boolean(student.email?.trim());
                  return (
                    <TableRow key={student.id}>
                      <TableCell>{getFullName(student)}</TableCell>
                      <TableCell>{student.email || '-'}</TableCell>
                      <TableCell>{student.phone || '-'}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            onClick={() => navigate(`${ROUTES.ADMIN_STUDENTS}/${student.id}`)}
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {!joined && hasEmail && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5"
                              onClick={() => openInviteModal(student)}
                              title={invite ? 'Resend invite' : 'Invite'}
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEnrollModal(student);
                            }}
                            title="Enroll"
                          >
                            <UserPlus className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={isAddModalOpen}
        onClose={() => {
          setAddModalOpen(false);
          setAddStudentErrors({});
        }}
        title="Add Student"
      >
        <form className="flex flex-col max-h-[90vh] overflow-hidden" onSubmit={handleCreateSubmit}>
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-4">
            <Input
              label="First Name"
              required
              placeholder={placeholders.first_name}
              value={studentForm.first_name}
              error={addStudentErrors.first_name}
              onChange={(event) => {
                const v = stripDigitsFromName(event.target.value);
                setStudentForm((prev) => ({ ...prev, first_name: v }));
                if (addStudentErrors.first_name) setAddStudentErrors((e) => ({ ...e, first_name: '' }));
              }}
            />
            <Input
              label="Last Name"
              placeholder={placeholders.last_name}
              value={studentForm.last_name}
              error={addStudentErrors.last_name}
              onChange={(event) => {
                const v = stripDigitsFromName(event.target.value);
                setStudentForm((prev) => ({ ...prev, last_name: v }));
                if (addStudentErrors.last_name) setAddStudentErrors((e) => ({ ...e, last_name: '' }));
              }}
            />
            <Input
              label="Admission Number"
              required
              placeholder={placeholders.admission_no}
              value={studentForm.admission_no}
              error={addStudentErrors.admission_no}
              onChange={(event) => {
                const v = sanitizeAdmissionNumber(event.target.value);
                setStudentForm((prev) => ({ ...prev, admission_no: v }));
                if (addStudentErrors.admission_no) setAddStudentErrors((e) => ({ ...e, admission_no: '' }));
              }}
            />
            <Input
              label="Email"
              type="email"
              maxLength={EMAIL_MAX_LENGTH}
              placeholder={placeholders.email}
              value={studentForm.email}
              error={addStudentErrors.email}
              onChange={(event) => {
                const value = event.target.value.slice(0, EMAIL_MAX_LENGTH);
                setStudentForm((prev) => ({ ...prev, email: value }));
                const trimmed = value.trim();
                if (trimmed && !validateEmail(trimmed)) {
                  setAddStudentErrors((e) => ({ ...e, email: 'Enter a valid email address' }));
                } else {
                  setAddStudentErrors((e) => ({ ...e, email: '' }));
                }
              }}
            />
            <div className="w-full">
              <label
                className="block text-sm font-medium text-app-text-muted dark:text-gray-300 mb-1"
                htmlFor="add-student-phone"
              >
                Phone
              </label>
              <PhoneInput
                country="us"
                value={studentForm.phone?.replace(/^\+/, '') ?? ''}
                onChange={(value) => {
                  setStudentForm((prev) => ({
                    ...prev,
                    phone: value ? `+${value}` : '',
                  }));
                  if (addStudentErrors.phone) setAddStudentErrors((e) => ({ ...e, phone: '' }));
                }}
                inputStyle={{
                  width: '100%',
                  height: '42px',
                  borderRadius: '0.5rem',
                  borderColor: addStudentErrors.phone ? 'var(--error-500, #ef4444)' : undefined,
                }}
                containerStyle={{ width: '100%' }}
                dropdownStyle={{ zIndex: 9999 }}
                buttonStyle={{ borderRadius: '0.5rem 0 0 0.5rem' }}
                inputProps={{ name: 'phone', id: 'add-student-phone' }}
                containerClass="add-student-phone-input"
              />
              {addStudentErrors.phone && (
                <p className="mt-1 text-sm text-error-600 dark:text-error-400">{addStudentErrors.phone}</p>
              )}
            </div>
            <Select
              label="Gender"
              options={[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
                { value: 'other', label: 'Other' },
              ]}
              value={studentForm.gender || 'male'}
              onChange={(event) => setStudentForm((prev) => ({ ...prev, gender: event.target.value }))}
              helperText={placeholders.gender}
            />
            <Input
              label="Date of Birth"
              type="date"
              placeholder={placeholders.dob}
              value={studentForm.dob || ''}
              onChange={(event) => setStudentForm((prev) => ({ ...prev, dob: event.target.value }))}
            />
          </div>

          <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/80 flex items-start justify-between gap-4 py-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="form-checkbox h-4 w-4 rounded"
                checked={sendInviteNow}
                onChange={(event) => setSendInviteNow(event.target.checked)}
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Send invite now
              </span>
            </label>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setAddModalOpen(false)} type="button">
                Cancel
              </Button>
              <Button type="submit" isLoading={isCreating}>
                Create
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isInviteModalOpen} onClose={() => setInviteModalOpen(false)} title="Invite Student">
        <form className="space-y-4" onSubmit={handleInviteSubmit}>
          <Input
            label="Email"
            required
            type="email"
            placeholder={placeholders.invite_email}
            value={inviteForm.email}
            onChange={(event) => setInviteForm((prev) => ({ ...prev, email: event.target.value }))}
            readOnly
          />
          <Input
            label="Invitee Name"
            required
            placeholder={placeholders.invitee_name}
            value={inviteForm.invitee_name}
            onChange={(event) => setInviteForm((prev) => ({ ...prev, invitee_name: event.target.value }))}
            readOnly
          />
          <Input
            label="Notes"
            placeholder={placeholders.invite_notes}
            value={inviteForm.notes}
            onChange={(event) => setInviteForm((prev) => ({ ...prev, notes: event.target.value }))}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setInviteModalOpen(false)} type="button">
              Cancel
            </Button>
            <Button type="submit" isLoading={isInviting}>
              Send Invite
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isEnrollModalOpen} onClose={() => setIsEnrollModalOpen(false)} title="Enroll Student">
        <form className="space-y-4" onSubmit={handleEnrollSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Student
            </label>
            <Input
              value={
                selectedStudentForEnroll
                  ? getFullName(selectedStudentForEnroll) || selectedStudentForEnroll.admission_no || '-'
                  : ''
              }
              readOnly
              disabled
            />
          </div>

          <Select
            label="Class"
            required
            options={[
              { label: placeholders.enroll_class, value: '' },
              ...classes.map((cls) => ({ label: cls.name, value: cls.id })),
            ]}
            value={enrollForm.class_id}
            onChange={handleClassChangeForEnroll}
            error={!enrollForm.class_id ? 'Class is required' : undefined}
          />

          <Select
            label="Section (optional)"
            options={[
              { label: placeholders.enroll_section, value: '' },
              ...filteredSections.map((sec) => ({
                label: sec.display_name || sec.name,
                value: sec.id,
              })),
            ]}
            value={enrollForm.section_id}
            onChange={handleSectionChangeForEnroll}
            disabled={!enrollForm.class_id}
          />

          <Input
            label="Academic Year"
            required
            placeholder={placeholders.enroll_academic_year}
            value={enrollForm.academic_year_key}
            onChange={(event) => setEnrollForm((prev) => ({ ...prev, academic_year_key: event.target.value }))}
          />

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setIsEnrollModalOpen(false)} type="button">
              Cancel
            </Button>
            <Button type="submit" isLoading={isEnrolling}>
              Enroll
            </Button>
          </div>
        </form>
      </Modal>

      <StudentBulkImportModal
        isOpen={isImportModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImportSuccess={loadStudents}
      />
    </Layout>
  );
};
